import { useCallback, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@mui/material';
import { format } from 'date-fns';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import {
  usePaintRegionAnalyticsByStructuresLazyQuery,
  usePlatformQuery,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { platformName as platformNameAtom, snackbarMessage } from '@/state/atoms';
import * as analysisState from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import { buildCombinedWorkbook } from '@/components/Structures/PaintRegionAnalyticsExport';

type Props = {
  structureIds: string[];
  platformName?: string;
};

export const ExportPaintRegionAnalyticsButton = ({ structureIds, platformName }: Props) => {
  const router = useRouter();
  const { platformId } = router.query;
  const { showSnackBar } = useSnackBarMessage({ atom: snackbarMessage });
  const unitSystem = useAtomValue(analysisState.unitSystem);
  const setPlatformName = useSetAtom(platformNameAtom);
  const { data: platformData } = usePlatformQuery({
    variables: { platformId: platformId as string },
    skip: Boolean(platformName) || !platformId || typeof platformId !== 'string',
  });
  useEffect(() => {
    if (!platformName && platformData?.platform?.name) {
      setPlatformName(platformData.platform.name);
    }
  }, [platformName, platformData?.platform?.name, setPlatformName]);
  const resolvedPlatformName = platformName || platformData?.platform?.name || 'platform';
  const [fetchAnalytics, { loading }] = usePaintRegionAnalyticsByStructuresLazyQuery({
    fetchPolicy: 'network-only',
  });

  const handleExport = useCallback(async () => {
    if (!structureIds.length) {
      showSnackBar('No active deployments to export', 'warning');
      return;
    }

    try {
      const { data } = await fetchAnalytics({
        variables: {
          input: {
            structureIds,
          },
        },
      });

      const analytics = data?.paintRegionAnalyticsByStructures;
      if (!Array.isArray(analytics) || analytics.length === 0) {
        showSnackBar('No analytics data available', 'info');
        return;
      }

      const workbook = buildCombinedWorkbook(analytics, unitSystem);
      const date = format(new Date(), 'yyyy-MM-dd');
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const baseName = resolvedPlatformName;

      const link = document.createElement('a');
      link.href = url;
      link.download = `${baseName}-${date}.xlsx`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackBar('Failed to export analytics', 'error');
    }
  }, [fetchAnalytics, showSnackBar, structureIds, unitSystem, resolvedPlatformName]);

  return (
    <Button
      variant="outlined"
      size="small"
      disabled={loading || !structureIds.length}
      onClick={handleExport}
      startIcon={
        <AbyssIcon iconName="file-download-line" style={{ height: '1.6rem', width: '1.6rem' }} />
      }
      sx={{
        color: abyssColors.primary[500],
        borderColor: abyssColors.primary[300],
        '&:hover': {
          borderColor: abyssColors.primary[500],
          backgroundColor: abyssColors.primary[50],
        },
      }}
    >
      {loading ? 'Exporting...' : 'Export to Excel'}
    </Button>
  );
};
